// @contexts/ClientProjectClient.context.tsx
import React, { createContext, useContext, ReactNode, useMemo } from "react";
import type { OptionTs } from "@ps-bridge/interop";
import * as Option from "@utils/Option";
import type { ClientProjectClient } from '@services/clients/ClientProjectClient';
import { fromInteropClientProjectClient } from '@services/clients/ClientProjectClient';
import { createDefaultClientProjectClient } from "@src/purescript/interop";
import { StubClientProjectClient } from "@services/stubs/StubClientProjectClient";
import { enabledFeatures, FeaturesEnum } from "@src/utils/enabledFeatures";
import { useJwtController } from "@contexts/Jwt.context";
import { requireContext } from "@contexts/requireContext";

const none = <A,>(): OptionTs<A> => Option.none();
const ClientProjectClientContext = createContext<OptionTs<ClientProjectClient>>(none());

export const ClientProjectClientProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { markUnauthorized } = useJwtController();
  const clientProjectClient = useMemo<ClientProjectClient>(() => {
    if (enabledFeatures[FeaturesEnum.StubClients]) {
      return new StubClientProjectClient();
    }
    return fromInteropClientProjectClient(createDefaultClientProjectClient(markUnauthorized));
  }, [markUnauthorized]);

  const contextValue = useMemo(
    () => Option.some(clientProjectClient),
    [clientProjectClient]
  );

  return (
    <ClientProjectClientContext.Provider value={contextValue}>
      {children}
    </ClientProjectClientContext.Provider>
  );
};

export const useClientProjectClient = (): ClientProjectClient => {
  return requireContext(
    useContext(ClientProjectClientContext),
    "useClientProjectClient must be used within a ClientProjectClientProvider",
  );
};
